import { useState, useEffect, useRef } from "react";
import {
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Chip,
	Tooltip,
	CircularProgress,
	Snackbar,
	Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import instance from "../../apiServices/instance";
import LinkIcon from "@mui/icons-material/Link";
import socketService from "../../services/socketService";
import CircleIcon from "@mui/icons-material/Circle";
import {
	companyInformation,
	getCompanyDetailsApi,
} from "../../apiServices/services/CompanyServices";
import { useNavigate } from "react-router-dom";

const AmlAndKyc = ({ onBack }) => {
	const [loading, setLoading] = useState(false);
	const [directors, setDirectors] = useState([]);
	const [allVerifiedStatus, setAllVerifiedStatus] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [openSnackBar, setOpenSnackBar] = useState(false);
	const socketSetupComplete = useRef(false);
	const navigate = useNavigate();

	const getDirectorsStatus = async () => {
		setLoading(true);
		let accountId = localStorage.getItem("accountId");
		if (!accountId) {
			const response = await getCompanyDetailsApi();
			if (response?.attributes?.results[0]) {
				const result = response.attributes.results[0];
				if (result?.xama?.account_id) {
					localStorage.setItem("accountId", result?.xama.account_id);
					accountId = result?.xama.account_id;
				} else {
					onBack();
				}
			}
		}

		try {
			const { data } = await instance.get(
				`/api/xama/verification-status/${accountId}`
			);

			setAllVerifiedStatus(data.all_verified);
			// Transform the contacts array to ensure consistent structure
			const transformedDirectors = data.contacts.map((contact) => ({
				contact_id: contact.contact_id,
				name: contact.name,
				url: contact.url || "#",
				aml: contact.aml || { status: "pending", ready: false },
				onboarding: contact.onboarding || {
					status: "pending",
					ready: false,
				},
				verification_complete: contact.verification_complete || false,
				last_updated: contact.last_updated || new Date(),
				error: contact.error,
			}));

			setDirectors(transformedDirectors);

			// Only set up socket listeners once
			if (!socketSetupComplete.current) {
				// Subscribe to socket updates for this account
				socketService.subscribeToVerification(accountId);

				// Set up listeners for socket events
				socketService.onOnboardingUpdate((data) => {
					handleStatusUpdate(data, "onboarding");
				});

				socketService.onAmlUpdate((data) => {
					handleStatusUpdate(data, "aml");
				});

				socketSetupComplete.current = true;
			}
		} catch (error) {
			console.error("Error fetching verification status:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleStatusUpdate = (socketData, type) => {
		if (!socketData || !socketData.data) {
			console.warn("Received invalid socket data", socketData);
			return;
		}

		const updateData = socketData.data;

		// Update director status in the list
		setDirectors((prev) => {
			return prev.map((director) => {
				if (director.contact_id === updateData.contactId) {
					const now = new Date();

					if (type === "aml") {
						return {
							...director,
							aml: {
								...director.aml,
								status: updateData.status,
								ready:
									updateData.status.toLowerCase() ===
										"completed" ||
									updateData.status.toLowerCase() === "ready",
								last_updated: now,
							},
							last_updated: now,
							verification_complete:
								director.onboarding?.ready &&
								(updateData.status.toLowerCase() ===
									"completed" ||
									updateData.status.toLowerCase() ===
										"ready"),
						};
					} else if (type === "onboarding") {
						return {
							...director,
							onboarding: {
								...director.onboarding,
								status: updateData.status,
								ready:
									updateData.status.toLowerCase() ===
										"completed" ||
									updateData.status.toLowerCase() === "ready",
								last_updated: now,
							},
							last_updated: now,
							verification_complete:
								(updateData.status.toLowerCase() ===
									"completed" ||
									updateData.status.toLowerCase() ===
										"ready") &&
								director.aml?.ready,
						};
					}
				}
				return director;
			});
		});
	};

	const handleIsCompanyIncorporatedManually = async () => {
		const data = await companyInformation();
		if (data.isCompanyIncorporatedManually) {
			console.log("Company is incorporated manually");
			navigate("/get-started");
			return;
		}
	};

	useEffect(() => {
		// Fetch data only once when component mounts
		getDirectorsStatus();

		handleIsCompanyIncorporatedManually();

		// Cleanup function to unsubscribe when component unmounts
		return () => {
			const accountId = localStorage.getItem("accountId");
			if (accountId) {
				socketService.unsubscribeFromVerification(accountId);
			}
			socketService.removeAllListeners();
			socketSetupComplete.current = false;
		};
	}, []); // Empty dependency array ensures this runs only once on mount

	const updatedShareHolders = async (accountId) => {
		await instance.post(`/api/xama/updateShareHolders`, {
			accountId,
		});
	};

	useEffect(() => {
		if (allVerifiedStatus) {
			let accountId = localStorage.getItem("accountId");
			updatedShareHolders(accountId);
		}
	}, [allVerifiedStatus]);

	const handleCopy = (url) => {
		if (!url || url === "#") {
			alert("No URL available to copy.");
			return;
		}

		navigator.clipboard
			.writeText(url)
			.then(() => alert("URL copied to clipboard!"))
			.catch((err) => console.error("Failed to copy:", err));
	};

	const getStatusColor = (status) => {
		if (!status) return "#9e9e9e"; // Gray for missing status

		const statusLower = status.toLowerCase();

		if (statusLower === "completed" || statusLower === "ready")
			return "#66bb6a"; // Green for completed
		if (statusLower === "in_review" || statusLower === "review_completed")
			return "#ffa726"; // Orange for in review
		if (statusLower === "error") return "#9e9e9e"; // Red for error
		if (statusLower === "initiated") return "#42a5f5"; // Blue for initiated
		if (statusLower === "pending") return "#9e9e9e"; // Gray for pending

		return "#f28b82"; // Default red for "Information Required" or other statuses
	};

	const getStatusLabel = (statusObject) => {
		console.log("statusObject", statusObject);
		if (!statusObject) return "Pending";

		const status = statusObject.status;
		if (!status || status == "ERROR") return "Pending";

		return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
	};

	const handleRefresh = () => {
		getDirectorsStatus();
	};

	if (loading && directors.length === 0) {
		return (
			<Box
				sx={{
					backgroundColor: "white",
					padding: "2rem 3.5rem",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
					}}>
					<CircularProgress size={40} sx={{ mb: 2 }} />
					<Typography>Loading verification status...</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				backgroundColor: "white",
				padding: "2rem 3.5rem",
				height: "100%",
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant='h6' fontWeight={700} color='black'>
					AML and KYC
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					{loading && (
						<>
							<CircularProgress size={20} sx={{ mr: 1 }} />
							<Typography variant='body2' color='text.secondary'>
								Refreshing...
							</Typography>
						</>
					)}
				</Box>
			</Box>

			<p style={{ margin: "1rem 0", width: "85%", color: "#000" }}>
				Before we can review and submit your company details to
				Companies House for incorporation, we are legally obligated to
				undertake an AML & KYC on the Directors of the business.
			</p>

			<p style={{ margin: "0 0 2rem 0", width: "85%", color: "#000" }}>
				Please ensure each of the director/s below has completed the AML
				& KYC check using the links provided.{" "}
				<strong>Real-time status updates will appear below.</strong>
			</p>

			<TableContainer
				component={Paper}
				sx={{ boxShadow: "none", border: "none" }}>
				<Table sx={{ border: "none" }}>
					<TableHead sx={{ backgroundColor: "#f5f6fa" }}>
						<TableRow>
							<TableCell>
								<strong>Director Name</strong>
							</TableCell>
							<TableCell>
								<strong>Get Started</strong>
							</TableCell>
							<TableCell>
								<strong>Unique Link</strong>
							</TableCell>
							<TableCell>
								<strong>AML Status</strong>
							</TableCell>
							<TableCell>
								<strong>KYC Status</strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{directors.length > 0 ? (
							directors.map((director, index) => (
								<TableRow key={index} sx={{ border: "none" }}>
									<TableCell
										sx={{
											textTransform: "capitalize",
										}}>
										{director.name}
									</TableCell>
									<TableCell>
										{director.url &&
										director.url !== "#" ? (
											<>
												<a
													href={director.url}
													target='_blank'
													rel='noopener noreferrer'
													style={{
														textDecoration: "none",
														color: "#1976d2",
													}}>
													Upload information
												</a>
												<IconButton
													size='small'
													color='primary'
													onClick={() =>
														window.open(
															director.url,
															"_blank"
														)
													}>
													<OpenInNewIcon fontSize='small' />
												</IconButton>
											</>
										) : (
											"No link available"
										)}
									</TableCell>
									<TableCell>
										<Button
											sx={{
												textTransform: "capitalize",
											}}
											color='primary'
											onClick={() =>
												handleCopy(director.url)
											}
											endIcon={
												<LinkIcon fontSize='small' />
											}
											disabled={
												!director.url ||
												director.url === "#"
											}>
											Copy & Share URL
										</Button>
									</TableCell>
									<TableCell>
										<Tooltip
											title={
												director.aml?.last_updated
													? `Last update: ${new Date(
															director.aml.last_updated
													  ).toLocaleString()}`
													: "No updates yet"
											}>
											<Chip
												label={getStatusLabel(
													director.aml
												)}
												sx={{
													backgroundColor:
														getStatusColor(
															director.aml?.status
														),
													color: "white",
												}}
											/>
										</Tooltip>
									</TableCell>
									<TableCell>
										<Tooltip
											title={
												director.onboarding
													?.last_updated
													? `Last update: ${new Date(
															director.onboarding.last_updated
													  ).toLocaleString()}`
													: "No updates yet"
											}>
											<Chip
												label={getStatusLabel(
													director.onboarding
												)}
												sx={{
													backgroundColor:
														getStatusColor(
															director.onboarding
																?.status
														),
													color: "white",
												}}
											/>
										</Tooltip>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={6} align='center'>
									No directors found. Please add directors to
									proceed with verification.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<Typography variant='body1' mt='1.5rem'>
					If your AML KYC is <strong>complete</strong> but the status
					remains <strong>pending</strong>, please click the{" "}
					<strong>refresh</strong> button below.
				</Typography>
				{!allVerifiedStatus ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "1rem",
						}}>
						<button
							style={{
								textDecoration: "none",
								color: "black",
								marginTop: "2rem",
								display: "inline-block",
								background: "#ccc",
								padding: "0.5rem 1rem",
								borderRadius: "5px",
							}}
							onClick={() => {
								setOpenSnackBar(true);
								setSnackbarMessage(
									"AML and Kyc Process Not Completed"
								);
							}}>
							Next
						</button>
						<button
							style={{
								textDecoration: "none",
								color: "black",
								marginTop: "2rem",
								display: "inline-block",
								background: "#eee",
								padding: "0.5rem 1rem",
								borderRadius: "5px",
							}}
							onClick={handleRefresh}>
							Refresh
						</button>
					</Box>
				) : (
					<Link
						style={{
							textDecoration: "none",
							color: "#FFF",
							marginTop: "2rem",
							display: "inline-block",
							background: "#FF0000",
							padding: "0.5rem 1rem",
							borderRadius: "5px",
						}}
						to='/get-started'>
						Next
					</Link>
				)}
			</TableContainer>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				open={openSnackBar}
				autoHideDuration={3000}
				onClose={() => {
					setOpenSnackBar(false);
				}}>
				<Alert
					onClose={() => {
						setOpenSnackBar(false);
					}}
					severity='error'
					variant='filled'
					sx={{ width: "100%" }}>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default AmlAndKyc;
